/**
 * Exposure Indicator (OI net GEX/DEX/VEX/CEX time series)
 *
 * Renders the full-book net exposure sums from the OI exposure rows
 * (net_gex/net_dex/net_vex/net_cex dealer-weighted, net_u_* absolute) as colored
 * baseline series in a sub-pane below the main chart.
 * Uses chart.addPane() so the pane shares the main chart's time axis.
 *
 * Descriptive only: the series re-evaluates a static settled-OI book as
 * spot and remaining time move; it is not a flow or direction signal.
 *
 * Display units follow the standard vendor conventions: GEX is dollars of
 * delta change per 1% spot move, DEX is dollar delta notional, VEX is per
 * IV point (1% vol), CEX is per calendar day. The stored payload keeps the
 * raw kernel units (per 100% move / per vol unit / per year); UNIT_SCALE
 * converts at render time only.
 *
 * Uses one series per trading day per metric to avoid lines connecting
 * across overnight gaps (lightweight-charts v5 does not support whitespace
 * gaps for line-based series).
 */

import { BaselineSeries } from 'lightweight-charts';

// ── Color / config constants ────────────────────────────────────────────────

const BASELINE_COLORS = {
  gex: {
    topLineColor: '#3b82f6',                        // blue (matches Exposure Profile gamma)
    topFillColor1: 'rgba(59, 130, 246, 0.12)',
    topFillColor2: 'rgba(59, 130, 246, 0.05)',
    bottomLineColor: '#f97316',                     // orange (negative net gamma)
    bottomFillColor1: 'rgba(249, 115, 22, 0.05)',
    bottomFillColor2: 'rgba(249, 115, 22, 0.12)',
  },
  dex: {
    topLineColor: '#ef4444',                        // red (matches Exposure Profile delta)
    topFillColor1: 'rgba(239, 68, 68, 0.12)',
    topFillColor2: 'rgba(239, 68, 68, 0.05)',
    bottomLineColor: '#22c55e',                     // green (negative net delta)
    bottomFillColor1: 'rgba(34, 197, 94, 0.05)',
    bottomFillColor2: 'rgba(34, 197, 94, 0.12)',
  },
  vex: {
    topLineColor: '#a855f7',                        // purple (matches Exposure Profile vanna)
    topFillColor1: 'rgba(168, 85, 247, 0.12)',
    topFillColor2: 'rgba(168, 85, 247, 0.05)',
    bottomLineColor: '#fbbf24',                     // yellow (negative net vanna)
    bottomFillColor1: 'rgba(251, 191, 36, 0.05)',
    bottomFillColor2: 'rgba(251, 191, 36, 0.12)',
  },
  cex: {
    topLineColor: '#06b6d4',                        // cyan (matches Exposure Profile charm)
    topFillColor1: 'rgba(6, 182, 212, 0.12)',
    topFillColor2: 'rgba(6, 182, 212, 0.05)',
    bottomLineColor: '#e11d48',                     // rose (negative net charm)
    bottomFillColor1: 'rgba(225, 29, 72, 0.05)',
    bottomFillColor2: 'rgba(225, 29, 72, 0.12)',
  },
};

const METRICS = ['gex', 'dex', 'vex', 'cex'];
const BASELINE_TITLES = { gex: 'GEX', dex: 'DEX', vex: 'VEX', cex: 'CEX' };

// Dimmed variants for the slow greeks (vanna/charm = vex/cex) while the
// full-book live smile is a hybrid (tail slices held at settled marks):
// the pane keeps drawing them, but faded, so a near-flat vanna line reads
// as "mostly settled marks, expected" rather than "frozen display".
const SLOW_DIM_COLORS = {
  vex: {
    topLineColor: 'rgba(168, 85, 247, 0.35)',
    topFillColor1: 'rgba(168, 85, 247, 0.04)',
    topFillColor2: 'rgba(168, 85, 247, 0.02)',
    bottomLineColor: 'rgba(251, 191, 36, 0.35)',
    bottomFillColor1: 'rgba(251, 191, 36, 0.02)',
    bottomFillColor2: 'rgba(251, 191, 36, 0.04)',
  },
  cex: {
    topLineColor: 'rgba(6, 182, 212, 0.35)',
    topFillColor1: 'rgba(6, 182, 212, 0.04)',
    topFillColor2: 'rgba(6, 182, 212, 0.02)',
    bottomLineColor: 'rgba(225, 29, 72, 0.35)',
    bottomFillColor1: 'rgba(225, 29, 72, 0.02)',
    bottomFillColor2: 'rgba(225, 29, 72, 0.04)',
  },
};

// Render-time unit conversion to the standard display conventions. Kernel
// units are per 100% move (gex), per vol unit (vex) and per year (cex).
const UNIT_SCALE = { gex: 0.01, dex: 1, vex: 0.01, cex: 1 / 365.25 };
const UNIT_SUFFIX = { gex: '/1%', dex: '', vex: '/1%vol', cex: '/day' };

// Payload fields per weighting mode: dealer = OI x dealer_ratio, absolute = OI only
const FIELDS_BY_MODE = {
  dealer: { gex: 'net_gex', dex: 'net_dex', vex: 'net_vex', cex: 'net_cex' },
  absolute: { gex: 'net_u_gex', dex: 'net_u_dex', vex: 'net_u_vex', cex: 'net_u_cex' },
};

// ── Utility functions ───────────────────────────────────────────────────────

/**
 * Format a number with M (million) / B (billion) / K (thousand) suffix.
 */
function formatExposure(value) {
  if (value == null || isNaN(value)) return '';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1e9) return sign + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return sign + (abs / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return sign + (abs / 1e3).toFixed(1) + 'K';
  return sign + abs.toFixed(0);
}

/**
 * Normalize an array of data points to [-100, +100] range.
 */
function normalizeData(data) {
  const values = data.filter(d => d.value != null).map(d => d.value);
  const maxAbs = Math.max(...values.map(Math.abs), 1);
  return {
    normalized: data.map(d => d.value != null
      ? { ...d, value: (d.value / maxAbs) * 100 }
      : d
    ),
    maxAbs,
  };
}

/**
 * Get the ET hour and minute from a Unix timestamp.
 */
function getETTime(timestamp) {
  const date = new Date(timestamp * 1000);
  const parts = date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).split(':');
  return { hour: parseInt(parts[0], 10), minute: parseInt(parts[1], 10) };
}

/**
 * Check if a Unix timestamp falls within RTH (9:30 AM - 4:00 PM ET).
 */
function isRTH(timestamp) {
  const { hour, minute } = getETTime(timestamp);
  const mins = hour * 60 + minute;
  return mins >= 570 && mins < 960;
}

/**
 * Get the ET calendar date string (MM/DD/YYYY) for a Unix timestamp.
 */
function getETDate(timestamp) {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
}

/**
 * Check if candles span more than one calendar day (ET).
 */
function isMultiDay(candles) {
  if (!candles || candles.length < 2) return false;
  return getETDate(candles[0].time) !== getETDate(candles[candles.length - 1].time);
}

// ── Indicator class ─────────────────────────────────────────────────────────

export class ExposureIndicator {
  /**
   * @param {object} chart - The main lightweight-charts IChartApi instance
   * @param {object} pane  - An IPaneApi from chart.addPane() — series are added here
   */
  constructor(chart, pane) {
    this.chart = chart;
    this.pane = pane;
    this.enabled = false;
    this._zeroPriceLine = null;
    this._mode = 'dealer';
    this._lastCandles = null;

    // Per-metric normalization denominators and per-day segment arrays
    this._maxAbs = { gex: 1, dex: 1, vex: 1, cex: 1 };
    this._segments = { gex: [], dex: [], vex: [], cex: [] };
    this._hybridDim = false;
  }

  // ── Segment management ──────────────────────────────────────────────────

  /**
   * Remove baseline segment series from the chart and clear arrays.
   */
  _removeBaselineSegments() {
    for (const metricKey of METRICS) {
      for (const s of this._segments[metricKey]) {
        try {
          this.chart.removeSeries(s);
        } catch (e) {
          // Series may already be detached during chart rebuild
        }
      }
      this._segments[metricKey].length = 0;
    }
    this._zeroPriceLine = null;
  }

  /**
   * Remove all series (used only on disable/teardown).
   */
  _removeAllSeries() {
    this._removeBaselineSegments();
  }

  /**
   * Create one BaselineSeries segment for a given metric.
   */
  _createBaselineSegment(metricKey, isFirst) {
    // Recreated segments (update() step 3) must come out already dimmed,
    // or a data refresh silently resets the hybrid fade.
    const colors = (this._hybridDim && SLOW_DIM_COLORS[metricKey])
      || BASELINE_COLORS[metricKey];
    return this.pane.addSeries(BaselineSeries, {
      priceScaleId: 'right',
      baseValue: { type: 'price', price: 0 },
      ...colors,
      lineWidth: 2,
      lastValueVisible: true,
      priceLineVisible: false,
      crosshairMarkerVisible: true,
      title: isFirst ? BASELINE_TITLES[metricKey] : '',
      priceFormat: {
        type: 'custom',
        formatter: (value) => {
          const s = formatExposure(
            (value / 100) * (this._maxAbs[metricKey] || 1) * UNIT_SCALE[metricKey]
          );
          return s ? s + UNIT_SUFFIX[metricKey] : s;
        },
      },
    });
  }

  // ── Public API ──────────────────────────────────────────────────────────

  /**
   * Fade the slow greeks (vanna/charm) while the full-book live smile is
   * a hybrid of live front slices and settled-mark tail slices. Applies
   * to existing segments immediately; _createBaselineSegment keeps the
   * state across segment recreation.
   */
  setHybridDim(dim) {
    const v = !!dim;
    if (this._hybridDim === v) return;
    this._hybridDim = v;
    for (const key of ['vex', 'cex']) {
      const colors = v ? SLOW_DIM_COLORS[key] : BASELINE_COLORS[key];
      for (const s of this._segments[key]) {
        try {
          s.applyOptions(colors);
        } catch (e) {
          // Series may already be detached during chart rebuild
        }
      }
    }
  }

  /**
   * Enable the Exposure indicator.
   */
  enable(series = []) {
    if (this.enabled) {
      if (series.length) this.update(series);
      return;
    }

    this.enabled = true;
    if (series.length) this.update(series);

    console.log('[ExposureIndicator] Enabled');
  }

  /**
   * Switch between dealer (OI x dealer ratio) and absolute (OI only)
   * weighting; re-renders from the cached candles without a refetch.
   */
  setMode(mode) {
    const next = mode === 'absolute' ? 'absolute' : 'dealer';
    if (next === this._mode) return;
    this._mode = next;
    if (this.enabled && this._lastCandles) this.update(this._lastCandles);
  }

  /**
   * Update exposure data from OI net-exposure candles.
   * Groups candles by day and creates one series segment per day per metric
   * so that lines don't connect across overnight gaps.
   */
  update(candles) {
    if (!this.enabled || !candles || candles.length === 0) {
      return;
    }
    this._lastCandles = candles;

    const fields = FIELDS_BY_MODE[this._mode];
    const multiDay = isMultiDay(candles);

    // ── Step 1: Group candles into per-day buckets ───────────────────────
    const daySegments = [];
    const emptySeg = () => ({ gex: [], dex: [], vex: [], cex: [] });

    if (multiDay) {
      const dayGroups = new Map();
      for (const candle of candles) {
        if (!isRTH(candle.time)) continue;
        const etDate = getETDate(candle.time);
        if (!dayGroups.has(etDate)) dayGroups.set(etDate, emptySeg());
        const day = dayGroups.get(etDate);
        for (const metricKey of METRICS) {
          const v = candle[fields[metricKey]];
          if (v != null) day[metricKey].push({ time: candle.time, value: v });
        }
      }
      for (const day of dayGroups.values()) {
        daySegments.push(day);
      }
    } else {
      // Single day — one segment, RTH candles only
      const seg = emptySeg();
      for (const candle of candles) {
        if (!isRTH(candle.time)) continue;
        for (const metricKey of METRICS) {
          const v = candle[fields[metricKey]];
          if (v != null) seg[metricKey].push({ time: candle.time, value: v });
        }
      }
      daySegments.push(seg);
    }

    const numSegments = daySegments.length;

    // ── Step 2: Normalize globally across all days ──────────────────────
    for (const metricKey of METRICS) {
      const allData = daySegments.flatMap(seg => seg[metricKey]);
      const { normalized, maxAbs } = normalizeData(allData);
      this._maxAbs[metricKey] = maxAbs;

      let offset = 0;
      for (const seg of daySegments) {
        const len = seg[metricKey].length;
        seg[metricKey] = normalized.slice(offset, offset + len);
        offset += len;
      }
    }

    // ── Step 3: Reconcile series count ──────────────────────────────────
    if (this._segments.gex.length !== numSegments) {
      this._removeBaselineSegments();

      for (let i = 0; i < numSegments; i++) {
        const isFirst = i === 0;
        for (const metricKey of METRICS) {
          this._segments[metricKey].push(this._createBaselineSegment(metricKey, isFirst));
        }
      }

      // Zero reference line on first GEX segment
      if (this._segments.gex.length > 0) {
        this._zeroPriceLine = this._segments.gex[0].createPriceLine({
          price: 0,
          color: '#666666',
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: false,
        });
      }

      // Configure scales after series creation (scales don't exist until series use them)
      try {
        this.pane.priceScale('right').applyOptions({
          visible: true,
          borderVisible: true,
          borderColor: '#2a2a2a',
          autoScale: true,
        });
      } catch (e) {
        console.warn('[ExposureIndicator] Price scale config error:', e);
      }
    }

    // ── Step 4: Set data on each segment series ─────────────────────────
    for (let i = 0; i < numSegments; i++) {
      for (const metricKey of METRICS) {
        this._segments[metricKey][i].setData(daySegments[i][metricKey]);
      }
    }

    const totalPoints = daySegments.reduce((sum, seg) => sum + seg.gex.length, 0);
    console.log('[ExposureIndicator] Updated with', totalPoints, 'data points across', numSegments, 'segment(s), mode', this._mode);
  }

  /**
   * Disable the Exposure indicator.
   */
  disable() {
    if (!this.enabled) {
      return;
    }

    this._removeAllSeries();

    try {
      this.pane.priceScale('right').applyOptions({ visible: false });
    } catch (e) {
      // Price scale may not exist during chart teardown
    }

    this.enabled = false;

    console.log('[ExposureIndicator] Disabled');
  }

  /**
   * Check if indicator is enabled.
   */
  isEnabled() {
    return this.enabled;
  }
}

export default ExposureIndicator;
